import { Channel } from "@redotech/cockroachdb-codegen/congodb/channel";
import { ProvisionType } from "@redotech/cockroachdb-codegen/congodb/provision-type";
import { Currency } from "@redotech/money/currencies";
import { moneyValueToNumber } from "@redotech/money/money-calculator";
import { PriceSet } from "@redotech/redo-common-model/money/price-set";
import { Tag } from "@redotech/redo-common-model/tag";
import { getCustomerPaidLandedCost } from "@redotech/redo-conversion-checkout-shipping-common/checkout-shipping-option";
import { getOrderDiscountLines } from "@redotech/redo-discounts-common/order-discount-lines";
import { moneySetToMoney } from "@redotech/redo-fulfillment-customs-common/order-duties";
import { Provider } from "@redotech/redo-order-common/provider";
import {
  BaseTrackable,
  normalizeTrackerDeliveryDate,
  ShopifyOrder,
  TrackableLineItem,
  TrackableType,
} from "@redotech/redo-order-tracking-trackable-common/trackable";
import { isCoverageFeeProduct } from "@redotech/redo-return-coverage-common/is-coverage-fee-product";
import { filterTruthy } from "@redotech/util/array";
import { convertArbitraryPhoneNumberToE164 } from "@redotech/util/phone-number";
import { getDropoffQrCodeUris } from "./dropoff/utils";
import { CustomerReturnCreatedAttachmentGenerationParameters } from "./email-builder";
import { Order } from "./order";
import {
  ExchangeItem,
  getCommercialInvoiceUrl,
  getReturner,
  MerchantAppReturn,
  Notes,
  Return,
  ReturnAddress,
  ReturnWarning,
  ReturnZod,
  ShipmentGroup,
} from "./return";
import { PickupLocation } from "./return-enums";
import { pickupLocationOptions } from "./return-flow";
import {
  getFieldsFromReturnTotalsSet,
  getProductFieldsFromReturnTotalsSet,
  ReturnTotalsSet,
} from "./returns/return-totals/return-totals-set";

export type ReturnTrackableProduct = (
  | Return["products"][number]
  | ReturnZod["products"][number]
) & {
  /**
   * Sourced from the corresponding Shopify line item in {@link returnToTrackable}.
   * The DB Return.products schema doesn't carry priceSet, so it's grafted on
   * here to let downstream renderers (e.g. tracking emails) use presentment
   * amounts on cross-border orders.
   */
  priceSet?: PriceSet;
};

export type Trackable = BaseTrackable &
  (
    | { type: TrackableType.ORDER }
    | { type: TrackableType.FULFILLMENT_GROUP }
    | {
        type: TrackableType.RETURN;
        returnProducts: ReturnTrackableProduct[];
        status: Return["status"];
        shipment: Return["shipment"] | ReturnZod["shipment"];
        shipments: Return["shipments"] | ReturnZod["shipments"];
        reShipments: Return["reShipments"] | ReturnZod["reShipments"];
        customerReturnCreatedAttachmentGenerationParameters: CustomerReturnCreatedAttachmentGenerationParameters; // best name ever
        notes?: Return["notes"] | ReturnZod["notes"];
        notesToCustomerOnApproval?: string;
        notesToCustomerOnProcessing?: string;
        imageUrlsOnApproval?: string[];
        imageUrlsOnProcessing?: string[];
        imageUrlsOnRejection?: string[];
        rejectReason?: string;
        merchantRejectionNotes?: string;
        returnFlowRejectionReasons?: string;
        pickup?: Return["pickup"] | ReturnZod["pickup"];
        returnType: Return["type"] | ReturnZod["type"];
        returnTypes: Return["returnType"] | ReturnZod["returnType"];
        markedForManualReview?: boolean;
        draftOrderURL?: string;
        warnings?: ReturnWarning[];
        inStoreReturn?: boolean;
        channel?: Channel;
        shipping_address?: ReturnAddress;
        merchant_address?: ReturnAddress;
        isManualLabel?: boolean;
        exchangeOrder: any[];
        /** Combined exchange items from advancedExchangeItems, exchange_for, and exchangeGroupItem */
        exchangeItems?: ExchangeItem[];
        form_label?: string;
        postage_label?: string;
        happyReturnsData?: any;
        blueYonder?: Return["blueYonder"];
        dropoffs?: Return["dropoffs"];
        pickupDayOfWeek?: string;
        pickupDetail?: string;
        packingSlipLink?: string;
        repair?: Return["repair"];
        refundValue?: number;
        returnExpirationDate?: Date | null;
        provision?: ProvisionType;
        fedExEasyReturnsQrCodes?: string[] | null;
        returnTotalValue?: number;
        shop_currency?: Currency;
        tags?: Tag[];
        shipmentGroups?: ShipmentGroup[];
        /** Store credit amount (excluding original order shipping) */
        storeCredit?: number;
        /** Store credit amount including original order shipping */
        storeCreditWithShipping?: number;
        /** Total value of exchange/new order items */
        newOrderValue?: number;
        /** Amount customer owes for exchange upsell */
        outstandingBalance?: number;
        /** Return shipping fee paid by customer */
        shippingFee?: number;
        /** Shipping fee deducted from refund/store credit */
        shippingFeeDeducted?: number;
        /** Exchange shipping fee */
        exchangeShippingFee?: number;
        /** Exchange shipping method name */
        exchangeShippingName?: string;
        /** Processing/adjustment fee value */
        processingFee?: number;
        /** Loyalty points deducted */
        loyaltyPointsDeducted?: number;
        /** Loyalty points refunded */
        loyaltyPointsRefunded?: number;
        /** The primary ecommerce provider for this return (e.g., shopify, commentsold) */
        provider?: Provider;
        /** Whether customer notifications are enabled for this return */
        sendCustomerNotificationsForProcessing: boolean;
        /** Whether the latest processing trigger for this return was manual */
        isManuallyProcessingReturn: boolean;
      }
  );

export function orderToTrackable(order: Order): Trackable {
  const shopifyOrder = mapShopify(order);
  const lineItems =
    order.shopify.line_items?.map(orderLineItemToTrackableLineItem) ?? [];
  const phoneNumber =
    order.shopify.phone ||
    order.shopify?.customer?.phone ||
    order.shopify?.shipping_address?.phone;
  return {
    type: TrackableType.ORDER,
    id: order._id.toString(),
    customer: {
      shopifyId: order.shopify.customer?.id,
      email:
        // https://community.shopify.com/t/differences-between-email-and-contact-email-in-orders-api/3455
        // In most cases you’ll find the email and contact_email on an order being the same thing, even if someone is checking out as a guest they’ll still need to provide either an e-mail address or phone number depending on the shop’s settings.
        // The main difference between the two is that in the event an email isn’t provided on an order the email field won’t be populated, but contact_email will consider other information like a customer account’s email address so that the field doesn’t necessarily end up being blank.
        order.shopify.email ||
        order.shopify.contact_email ||
        order.shopify?.customer?.email,
      phoneNumber: phoneNumber
        ? convertArbitraryPhoneNumberToE164(phoneNumber) || undefined
        : undefined,
      name: order.customer_name,
      firstName: order.shopify.customer?.first_name,
      lastName: order.shopify.customer?.last_name,
    },
    fulfillments: order.shopify.fulfillments.map((fulfillment) => ({
      id: fulfillment.id?.toString(),
      trackers: order.trackers
        .filter((t) => {
          try {
            return t.fulfillmentID.toString() === fulfillment.id?.toString();
          } catch (e) {
            console.error(
              `Error comparing tracker fulfillment ID ${t.fulfillmentID} to fulfillment ID ${fulfillment.id}, order ID ${order._id}`,
            );
            return false;
          }
        })
        .map((t) => normalizeTrackerDeliveryDate(t._tracker)),
      // fulfillment line items have less info than root line items
      lineItems: fulfillment.line_items.map((lineItem) => {
        const trackableLineItem =
          lineItems.find((li) => li.id.toString() === lineItem.id.toString()) ??
          orderLineItemToTrackableLineItem(lineItem);
        return {
          ...trackableLineItem,
          // the root line item quantity is the total quantity of the item in the order
          // the fulfillment line item quantity is the quantity of the item that was actually fulfilled in this fulfillment
          fulfilledQuantity: lineItem.quantity,
        };
      }),
      isShipped: !!fulfillment.shipment_status,
      updatedAt: fulfillment.updated_at
        ? new Date(fulfillment.updated_at)
        : undefined,
      tracking_numbers: fulfillment.tracking_numbers,
      status: fulfillment.status,
      shipment_status: fulfillment.shipment_status ?? undefined,
      tracking_company: fulfillment.tracking_company ?? undefined,
      tracking_url: fulfillment.tracking_url ?? undefined,
      locationId: fulfillment.location_id,
    })),

    lineItems:
      order.shopify.line_items?.map(orderLineItemToTrackableLineItem) ?? [],
    createdAt: new Date(order.createdAt),
    originalOrder: {
      id: order._id.toString(),
      shopify: shopifyOrder,
      landedCost: getCustomerPaidLandedCost(
        order.selectedRedoCheckoutShippingOptions,
      ),
    },
    currentEmailFlows:
      order.currentEmailFlows?.map((f) => ({
        ...f,
        continueDate: new Date(f.continueDate),
      })) ?? [],
    teamId: order.team.toString(),
    discount: order.discount,
    trackingEmailsSent: order.trackingEmailsSent,
    trackingWebhooksSent: order.trackingWebhooksSent,
    provider: order.provider,
  };
}

function getReturnTotalsProductOriginalPrice(
  returnProductId: string,
  fallbackPrice: string,
  returnTotalsSet?: ReturnTotalsSet,
): string {
  const productTotals = returnTotalsSet
    ? getProductFieldsFromReturnTotalsSet({
        returnTotalsSet,
        productId: returnProductId,
      })
    : undefined;
  return productTotals?.originalPrice?.toString() ?? fallbackPrice;
}

function getReturnTotalsProductDiscountPrice(
  returnProductId: string,
  returnTotalsSet?: ReturnTotalsSet,
): number | undefined {
  const newProductTotals = returnTotalsSet?.new?.productTotalsList.find(
    (p) => p.id.toString() === returnProductId.toString(),
  );

  return typeof newProductTotals?.originalValue.discountedPrice === "number"
    ? newProductTotals.originalValue.discountedPrice
    : newProductTotals?.originalValue.discountedPrice
      ? moneyValueToNumber(newProductTotals.originalValue.discountedPrice)
      : undefined;
}

export function returnToTrackable(
  return_: Return | MerchantAppReturn,
  order: Order,
  returnTotalsSet?: ReturnTotalsSet,
): Trackable {
  // returns don't track the same details about line items,
  // so we have to get the ones from the order object
  const lineItemPriceSetById = new Map<string, PriceSet>();
  for (const lineItem of order.shopify.line_items ?? []) {
    if (!lineItem.price_set) {
      continue;
    }
    lineItemPriceSetById.set(lineItem.id.toString(), {
      shopMoney: {
        amount: lineItem.price_set.shop_money.amount.toString(),
        currencyCode: lineItem.price_set.shop_money.currency_code as Currency,
      },
      presentmentMoney: {
        amount: lineItem.price_set.presentment_money.amount.toString(),
        currencyCode: lineItem.price_set.presentment_money
          .currency_code as Currency,
      },
    });
  }

  const returnProducts = (return_.products ?? []).map((product) => {
    const priceSet = lineItemPriceSetById.get(product.line_item_id.toString());
    const discountPrice = getReturnTotalsProductDiscountPrice(
      product._id.toString(),
      returnTotalsSet,
    );
    return {
      ...product,
      price: discountPrice?.toString() ?? product.price,
      deliveredDate: product.deliveredDate
        ? new Date(product.deliveredDate)
        : undefined,
      ...(priceSet
        ? {
            priceSet:
              discountPrice !== undefined
                ? {
                    shopMoney: priceSet.shopMoney,
                    presentmentMoney: {
                      ...priceSet.presentmentMoney,
                      amount: discountPrice.toFixed(2),
                    },
                  }
                : priceSet,
          }
        : {}),
    };
  });
  const lineItems = (order.shopify.line_items ?? [])
    .filter((lineItem) =>
      return_.products.some(
        (product) => product.line_item_id.toString() === lineItem.id.toString(),
      ),
    )
    .map((lineItem) => {
      const product = return_.products.find(
        (product) => product.line_item_id.toString() === lineItem.id.toString(),
      );
      if (!product) {
        throw new Error("Product not found");
      }
      // Find the corresponding ProductTotals to get the correct originalPrice
      const originalPrice = getReturnTotalsProductOriginalPrice(
        product._id.toString(),
        lineItem.price,
        returnTotalsSet,
      );

      return orderLineItemToTrackableLineItem({
        ...lineItem,
        price: parseFloat(originalPrice),
      });
    });

  const shipments =
    (return_.shipments?.length || 0) > 0
      ? return_.shipments
      : return_.shipment
        ? [return_.shipment]
        : [];

  const returner = getReturner(return_);
  if (!returner.email) {
    throw new Error("No email found on returner");
  }

  const pickupDate = return_.pickup?.pickupDate
    ? Temporal.PlainDate.from(return_.pickup.pickupDate)
    : undefined;
  const pickupDayOfWeek = pickupDate?.toLocaleString(undefined, {
    weekday: "long",
  });

  const packagePickupLocation = return_.pickup?.pickupLocation?.packageLocation;
  const packagePickupSpecialInstructions =
    return_.pickup?.pickupLocation?.specialInstructions;

  const packagePickupDetail = return_.pickup
    ? packagePickupLocation === PickupLocation.OTHER
      ? `with the following instructions: "${packagePickupSpecialInstructions}"`
      : pickupLocationOptions().get(packagePickupLocation as PickupLocation)
          ?.detail
    : undefined;
  const latestProcessingTrigger =
    return_.processingTriggers && return_.processingTriggers.length > 0
      ? return_.processingTriggers[return_.processingTriggers.length - 1]
      : undefined;

  const customerReturnCreatedAttachmentGenerationParameters: CustomerReturnCreatedAttachmentGenerationParameters =
    {
      shipments:
        return_.shipments?.map((shipment) => ({
          postage_label: shipment.postage_label ?? undefined,
          form_label: shipment.form_label!,
          _shipment: shipment._shipment,
          customs_forms: shipment.customs_forms ?? undefined,
        })) ?? [],
      variables: {
        postage_label: return_.postage_label ?? undefined, // Return Shipping Label
        form_label: return_.form_label ?? undefined, // Return Shipping QR Code
        commercialInvoice: getCommercialInvoiceUrl(return_.shipment)?.url,
      },
      pickup_details: return_.shipment?.pickup,
      manualLabelData: return_.manualLabelData
        ? {
            shipments:
              return_.manualLabelData.shipments?.map((shipment) => ({
                packingSlipUrl: shipment.packingSlipUrl ?? undefined,
                returnItemIds: shipment.returnItemIds,
              })) ?? undefined,
          }
        : undefined,
      dropoffQrCodeUrls: getDropoffQrCodeUris(return_),
    };

  // Combine exchange items from all sources:
  // 1. advancedExchangeItems - items from the advanced exchange cart
  // 2. exchange_for on products - variant exchange items
  // 3. exchangeGroupItem on products - exchange group items
  // Variant and exchange-group swaps apply the value of the returned item, not
  // the new item's retail price, so the email matches the portal (e.g. swapping
  // a $45 item for a $50 variant shows $45). Source the value from the enriched
  // returnProducts (discount-aware price/priceSet) while keeping the new item's
  // identity from exchange_for / exchangeGroupItem.
  const exchangeForItems: ExchangeItem[] = returnProducts
    .filter((p) => p.exchange_for)
    .map((p) => ({
      variantId: p.exchange_for!.variant_id,
      productId: p.exchange_for!.product_id,
      title: p.exchange_for!.product_title,
      variantTitle: p.exchange_for!.variant_title,
      images: p.exchange_for!.images,
      price: p.price ?? undefined,
      priceSet: p.priceSet ?? undefined,
      quantity: p.quantity,
      sku: p.exchange_for!.sku ?? undefined,
    }));

  const exchangeGroupItems: ExchangeItem[] = returnProducts
    .filter((p) => p.exchangeGroupItem)
    .map((p) => ({
      variantId: p.exchangeGroupItem!.variantId,
      productId: p.exchangeGroupItem!.productId,
      title: p.exchangeGroupItem!.title,
      variantTitle: p.exchangeGroupItem!.variantTitle,
      images: p.exchangeGroupItem!.imageSrc
        ? [p.exchangeGroupItem!.imageSrc]
        : [],
      price: p.price ?? undefined,
      priceSet: p.priceSet ?? undefined,
      quantity: p.quantity,
    }));

  const advancedExchangeItems: ExchangeItem[] = (
    return_.advancedExchangeItems ?? []
  ).map((item) => ({
    variantId: item.variantId,
    productId: item.productId ?? undefined,
    title: item.title,
    variantTitle: item.variantTitle,
    images: item.images ? filterTruthy(item.images) : undefined,
    price: item.price ?? undefined,
    priceSet: item.priceSet ?? undefined,
    itemValue: item.itemValue ?? undefined,
    itemValueSet: item.itemValueSet ?? undefined,
    quantity: item.quantity,
    sku: item.sku ?? undefined,
  }));

  const exchangeItems: ExchangeItem[] = [
    ...exchangeForItems,
    ...exchangeGroupItems,
    ...advancedExchangeItems,
  ];

  const returnTotalsCalculations = returnTotalsSet
    ? getFieldsFromReturnTotalsSet({ returnTotalsSet })
    : undefined;

  return {
    returnProducts,
    type: TrackableType.RETURN,
    status: return_.status,
    returnType: return_.type,
    returnTypes: return_.returnType,
    exchangeOrder: return_.exchangeOrder ?? [],
    exchangeItems: exchangeItems.length > 0 ? exchangeItems : undefined,
    id: return_._id.toString(),
    customer: {
      email: returner.email!,
      name: returner.name,
      firstName: returner.firstName || "",
      lastName: returner.lastName || "",
      shopifyId: order.shopify.customer?.id,
      phoneNumber: returner.phone || undefined,
    },
    fulfillments: (shipments ?? []).map((shipment) => {
      // Get items for this specific shipment by matching shipmentGroupID
      const shipmentGroupId = shipment.shipmentGroupID;
      const shipmentLineItems = shipmentGroupId
        ? return_.products
            .filter((product) => {
              const productGroupIds = [
                ...(product.shipmentGroupIDs ?? []),
                ...(product.shipmentGroupID ? [product.shipmentGroupID] : []),
              ];

              return productGroupIds.includes(shipmentGroupId);
            })
            .map((product) =>
              lineItems.find(
                (li) => li.id.toString() === product.line_item_id.toString(),
              ),
            )
            .filter((li): li is TrackableLineItem => li != null)
        : [];

      return {
        id: shipment._shipment.id,
        trackers: shipment._shipment.tracker
          ? [normalizeTrackerDeliveryDate(shipment._shipment.tracker)]
          : [],
        // Use shipment-specific items if available, fallback to all items
        lineItems: shipmentLineItems.length > 0 ? shipmentLineItems : lineItems,
        isShipped: !["unknown", "pre_transit"].includes(
          shipment._shipment.status,
        ),
        updatedAt: shipment._shipment.updated_at
          ? new Date(shipment._shipment.updated_at)
          : undefined,
        status: shipment._shipment.status,
        shipment_status: shipment._shipment.status,
      };
    }),
    lineItems,
    createdAt: new Date(return_.createdAt),
    originalOrder: {
      id: order._id.toString(),
      shopify: mapShopify(order),
      landedCost: getCustomerPaidLandedCost(
        order.selectedRedoCheckoutShippingOptions,
      ),
    },
    currentEmailFlows:
      return_.currentEmailFlows?.map((f) => ({
        ...f,
        continueDate: new Date(f.continueDate),
      })) ?? [],
    teamId: order.team.toString(),
    discount: return_.discount,
    shipment: return_.shipment,
    shipments: return_.shipments,
    reShipments: return_.reShipments,
    packingSlipLink: return_.packingSlipLink ?? undefined,
    customerReturnCreatedAttachmentGenerationParameters,
    notes: { notesToCustomer: return_.notes?.notesToCustomer ?? undefined },
    notesToCustomerOnApproval:
      return_.notes && "toCustomerOnApproval" in return_.notes
        ? (return_.notes as Notes).toCustomerOnApproval
        : undefined,
    notesToCustomerOnProcessing:
      return_.notes && "toCustomerOnProcessing" in return_.notes
        ? (return_.notes as Notes).toCustomerOnProcessing
        : undefined,
    imageUrlsOnApproval:
      return_.notes && "imageUrlsOnApproval" in return_.notes
        ? ((return_.notes as Notes).imageUrlsOnApproval ?? undefined)
        : undefined,
    imageUrlsOnProcessing: return_.processingInput?.customerImages ?? undefined,
    imageUrlsOnRejection: return_.processingInput?.customerImages ?? undefined,
    rejectReason:
      return_.rejectReason ||
      return_.processingInput?.customerRejectionNotes ||
      "No reason provided",
    merchantRejectionNotes:
      return_.processingInput?.customerRejectionNotes ?? undefined,
    trackingEmailsSent:
      return_.trackingEmailsSent?.map((email) => ({
        ...email,
        sentAt:
          typeof email.sentAt === "string"
            ? email.sentAt
            : (email.sentAt?.toISOString() ?? ""),
        s3URL: email.s3URL ?? undefined,
      })) ?? undefined,
    trackingWebhooksSent:
      return_.trackingWebhooksSent?.map((webhook) => ({
        ...webhook,
        sentAt:
          typeof webhook.sentAt === "string"
            ? webhook.sentAt
            : webhook.sentAt.toISOString(),
      })) ?? undefined,
    pickup: return_.pickup,
    markedForManualReview: return_.markedForManualReview,
    draftOrderURL: return_.draftOrderURL,
    warnings: return_.warnings,
    inStoreReturn: return_.inStoreReturn || return_.isPosReturn || undefined,
    channel: return_.channel,
    isManualLabel: return_.isManualLabel ?? undefined,
    merchant_address: return_.merchant_address ?? undefined,
    shipping_address: return_.shipping_address ?? undefined,
    form_label: return_.form_label ?? undefined,
    postage_label: return_.postage_label ?? undefined,
    happyReturnsData: return_.happyReturnsData,
    provider: return_?.provider ?? order.provider ?? Provider.SHOPIFY,
    pickupDayOfWeek,
    pickupDetail: packagePickupDetail,
    repair: return_.repair
      ? {
          ...return_.repair,
          invoiceId: return_.repair.invoiceId ?? undefined,
          invoiceURL: return_.repair.invoiceURL ?? undefined,
          shopifyOrderId: return_.repair.shopifyOrderId ?? undefined,
        }
      : undefined,
    blueYonder: return_.blueYonder,
    dropoffs: return_.dropoffs,
    refundValue: returnTotalsCalculations?.totalRefundWithoutShipping,
    returnExpirationDate: return_.expirationDate,
    provision: return_.provision,
    fedExEasyReturnsQrCodes: return_.blueYonder?.returns
      .map((ferRma) => ferRma.qrCodeUri)
      .filter((qrCodeUri) => qrCodeUri != null),
    returnTotalValue: returnTotalsCalculations?.totalReturnValue,
    shop_currency: order.shopify.currency,
    tags: return_.tags ?? [],
    shipmentGroups:
      return_.shipmentGroups?.map((group) => ({
        ...group,
        grams: group.grams ?? undefined,
        labelInstructions: group.labelInstructions ?? undefined,
        donationMatchIds: group.donationMatchIds ?? undefined,
      })) ?? [],
    // Additional return totals for cost summary
    storeCredit: returnTotalsCalculations?.totalStoreCredit,
    storeCreditWithShipping:
      returnTotalsCalculations?.totalStoreCreditWithShipping,
    newOrderValue: returnTotalsCalculations?.newOrderValue,
    outstandingBalance: returnTotalsCalculations?.displayCharge,
    shippingFee: returnTotalsCalculations?.shippingFee,
    exchangeShippingFee: returnTotalsCalculations?.exchangeShippingFee,
    exchangeShippingName: returnTotalsCalculations?.exchangeShippingName,
    processingFee: returnTotalsCalculations?.totalAdjustmentValue,
    loyaltyPointsDeducted: returnTotalsCalculations?.totalLoyaltyPointsDeducted,
    loyaltyPointsRefunded: returnTotalsCalculations?.totalLoyaltyPointsRefunded,
    sendCustomerNotificationsForProcessing:
      return_.processingInput?.sendNotifications ?? false,
    isManuallyProcessingReturn: latestProcessingTrigger?.type === "manual",
  };
}

export function mapShopify(order: Order): ShopifyOrder {
  const { shipping_address, billing_address, ...shopify } = order.shopify;
  const shippingAddress = shipping_address
    ? {
        name: shipping_address.name,
        address1: shipping_address.address1,
        address2: shipping_address.address2,
        city: shipping_address.city,
        province: shipping_address.province,
        provinceCode: shipping_address.province_code,
        zip: shipping_address.zip,
        country: shipping_address.country,
        countryCode: shipping_address.country_code,
        phone: shipping_address.phone,
      }
    : undefined;
  const billingAddress = billing_address
    ? {
        name: billing_address.name,
        address1: billing_address.address1,
        address2: billing_address.address2,
        city: billing_address.city,
        province: billing_address.province,
        provinceCode: billing_address.province_code,
        zip: billing_address.zip,
        country: billing_address.country,
        countryCode: billing_address.country_code,
        phone: billing_address.phone,
      }
    : undefined;
  return {
    id: shopify.id?.toString(),
    number: shopify.order_number,
    name: shopify.name,
    cancelReason: shopify.cancel_reason,
    contact_email: shopify.contact_email,
    createdAt: new Date(shopify.created_at),
    customer: shopify.customer ? { id: shopify.customer.id } : null,
    fulfillments: shopify.fulfillments.map((fulfillment) => ({
      id: fulfillment.id?.toString(),
      updatedAt: fulfillment.updated_at
        ? new Date(fulfillment.updated_at)
        : undefined,
      shipmentStatus: fulfillment.shipment_status || "unknown",
      isShipped: !["unknown", "pre_transit"].includes(
        fulfillment.shipment_status || "unknown",
      ),
      tracking: {
        numbers: fulfillment.tracking_numbers ?? [],
        urls: fulfillment.tracking_urls ?? [],
        company: fulfillment.tracking_company,
      },
      locationId: fulfillment.location_id,
    })),
    lineItems: shopify.line_items.map((line) => ({
      productId: line.product_id?.toString() ?? null,
    })),
    shippingLines:
      shopify.shipping_lines?.map((line: any) => ({
        title: line.title,
        price: parseFloat(line.price),
        discountedPrice: parseFloat(line.discounted_price),
        discountedPriceSet: line.discounted_price_set,
        priceSet: line.price_set,
        carrierIdentifier: line.carrier_identifier,
        code: line.code,
      })) ?? [],
    presentmentCurrency: shopify.presentment_currency,
    priceBeforeDiscounts: parseFloat(shopify.total_line_items_price),
    totalDiscounts: parseFloat(shopify.total_discounts ?? "0"),
    totalDiscountsSet: shopify.total_discounts_set
      ? {
          presentment_money: {
            amount: parseFloat(
              shopify.total_discounts_set.presentment_money.amount.toString(),
            ),
          },
        }
      : undefined,
    currentSubtotalPriceSet: shopify.current_subtotal_price_set,
    discounts: {
      individualDiscounts: shopify.discount_applications?.map(
        ({ value, value_type }: any) => ({
          discountType: value_type,
          amount: parseFloat(value),
        }),
      ),
    },
    discountLines: getOrderDiscountLines(order.shopify).map((line) => ({
      label: line.label,
      amount: line.amount.amount,
    })),
    currentTotalTaxSet: shopify.current_total_tax_set || null,
    currentTotalPriceSet: shopify.current_total_price_set,
    currentTotalDuties: moneySetToMoney(shopify.current_total_duties_set),
    currentTotalAdditionalFees: moneySetToMoney(
      shopify.current_total_additional_fees_set,
    ),
    shippingAddress,
    billingAddress,
    note: shopify.note,
    tags: shopify.tags?.split(",").map((tag: string) => tag.trim()) ?? [],
    sourceName: shopify.source_name,
    financialStatus: shopify.financial_status as
      | "authorized"
      | "paid"
      | "partially_paid"
      | "partially_refunded"
      | "pending"
      | "refunded"
      | "voided"
      | undefined,
    po_number: shopify.po_number,
  };
}

function orderLineItemToTrackableLineItem(
  lineItem: Order["shopify"]["line_items"][number],
): TrackableLineItem {
  const isTip = !!lineItem.tip;
  return {
    id: lineItem.id,
    title: isTip ? "Contribution" : lineItem.title,
    productId: lineItem.product_id?.toString() ?? undefined,
    variantId: lineItem.variant_id?.toString() ?? undefined,
    variantTitle: lineItem.variant_title,
    quantity: lineItem.current_quantity ?? lineItem.quantity,
    price: parseFloat(lineItem.price),
    priceSet: lineItem.price_set
      ? {
          ...lineItem.price_set,
          presentment_money: {
            ...lineItem.price_set.presentment_money,
            amount: parseFloat(
              lineItem.price_set.presentment_money.amount.toString(),
            ),
          },
        }
      : undefined,
    totalDiscountSet: lineItem.total_discount_set
      ? {
          ...lineItem.total_discount_set,
          presentment_money: {
            ...lineItem.total_discount_set.presentment_money,
            amount: parseFloat(
              lineItem.total_discount_set.presentment_money.amount.toString(),
            ),
          },
        }
      : undefined,
    totalDiscount: lineItem.total_discount,
    isRedo: isCoverageFeeProduct({
      product: { vendor: lineItem.vendor, sku: lineItem.sku },
    }),
    requiresShipping: lineItem.requires_shipping,
    image: isTip
      ? { src: "https://assets.getredo.com/marketing/donation.png" }
      : lineItem.image?.src
        ? lineItem.image
        : {
            src: `https://placehold.co/150?text=${lineItem.title?.toLowerCase().includes("return") ? "Return" : (lineItem.title ?? "Product")}`,
          },
    tags: lineItem.tags ?? [],
    properties:
      lineItem.properties?.reduce(
        (acc, prop) => {
          acc[prop.name] = prop.value;
          return acc;
        },
        {} as Record<string, string>,
      ) ?? {},
    sku: lineItem.sku,
    green_return: lineItem.green_return,
    vendor: lineItem.vendor,
    isTip,
  };
}
